import { useEffect, useRef, useState } from "react";
import BaseSelector from "components/ui/BaseSelector";

/**
 * 이메일 입력 — [아이디] @ [도메인 선택] 한 줄 (가입폼 표준 모양).
 *
 * 도메인을 직접 타이핑하지 않게 해서 `naver.con`·`gmial.com` 같은 오타를 원천 차단한다.
 * (오타 주소로 보낸 인증메일은 반송되고, 반송이 쌓이면 AWS 가 발송 계정을 정지시킨다.)
 * 회사 메일 등은 "직접입력"으로 계속 받을 수 있어야 하므로 마지막 항목으로 남긴다.
 *
 * value/onChange 는 **합쳐진 전체 이메일 문자열** 하나로 주고받는다 —
 * 쓰는 쪽(Register·AccountRecovery)의 검증·API 호출을 건드리지 않기 위해서다.
 *
 * CSS: 새로 만들지 않고 .field > label + .input-wrap > .wrapper 구조를 그대로 쓴다
 *  (같은 조합의 선례: components/muyeon/TimeRangeSelect.js, DateSelect.js, ui/InputWithButton.js).
 */

// 흔히 쓰는 순서대로. value 와 text 가 같아야 <select> 표시와 실제 도메인이 일치한다.
export const EMAIL_DOMAINS = [
  "naver.com",
  "gmail.com",
  "daum.net",
  "hanmail.net",
  "kakao.com",
  "nate.com",
  "outlook.com",
  "icloud.com",
];

const CUSTOM = "__custom__"; // 직접입력 선택값

const OPTIONS = [
  ...EMAIL_DOMAINS.map((d) => ({ value: d, text: d })),
  { value: CUSTOM, text: "직접입력" },
];

export default function EmailField({
  label = "이메일",
  required,
  value = "",
  onChange,
  isValid = true,
  message,
  isDisabled = false,
  placeholder = "아이디",
}) {
  // 합쳐진 값에서 앞/뒤를 나눈다. @ 가 여러 개면 마지막 것을 기준으로(로컬파트에 @ 는 사실상 없음).
  const at = String(value).lastIndexOf("@");
  const local = at >= 0 ? String(value).slice(0, at) : String(value);
  const domain = at >= 0 ? String(value).slice(at + 1) : "";

  // 목록에 없는 도메인(회사 메일 등)이면 직접입력 모드로 시작한다.
  const [custom, setCustom] = useState(() => !!domain && !EMAIL_DOMAINS.includes(domain));
  const customInputRef = useRef(null);

  // 값이 밖에서 바뀌어(초안 복원 등) 목록 밖 도메인이 들어오면 직접입력으로 전환.
  useEffect(() => {
    if (domain && !EMAIL_DOMAINS.includes(domain)) setCustom(true);
  }, [domain]);

  const emit = (l, d) => onChange(!l && !d ? "" : `${l}@${d}`);

  const onSelect = (e) => {
    const v = e.target.value;
    if (v === CUSTOM) {
      setCustom(true);
      emit(local, "");
      // 선택 직후 바로 타이핑할 수 있게 포커스를 옮긴다.
      setTimeout(() => customInputRef.current?.focus(), 0);
      return;
    }
    setCustom(false);
    emit(local, v);
  };

  return (
    <div className={`field ${!isValid ? "negative" : ""}`}>
      <label>
        {label}
        {required && <span className="required" aria-hidden="true">*</span>}
      </label>
      <div className="input-wrap">
        <div className="wrapper" style={{ display: "flex", gap: "0.8rem", alignItems: "center" }}>
          <div className="input-box" style={{ flex: 1, minWidth: 0 }}>
            <input
              className="input-inner"
              type="text"
              inputMode="email"
              autoComplete="username"
              placeholder={placeholder}
              value={local}
              onChange={(e) => emit(e.target.value.trim(), domain)}
              disabled={isDisabled}
              maxLength={64}
            />
          </div>
          <span aria-hidden="true">@</span>
          {/* 잠금(인증 완료) 상태에선 셀렉트 대신 잠긴 입력칸으로 — BaseSelector 에는 disabled 가 없어
              그대로 두면 입력칸만 잠기고 도메인은 바뀌는 어긋난 상태가 된다. */}
          {isDisabled ? (
            <div className="input-box" style={{ flex: 1, minWidth: 0 }}>
              <input className="input-inner" type="text" value={domain} disabled readOnly />
            </div>
          ) : custom ? (
            <div className="input-box" style={{ flex: 1, minWidth: 0 }}>
              <input
                ref={customInputRef}
                className="input-inner"
                type="text"
                inputMode="url"
                placeholder="도메인 입력"
                value={domain}
                onChange={(e) => emit(local, e.target.value.trim().toLowerCase())}
                disabled={isDisabled}
                maxLength={60}
              />
            </div>
          ) : (
            <div style={{ flex: 1, minWidth: 0 }}>
              <BaseSelector
                label=""
                value={domain}
                option={OPTIONS}
                placeholder="선택"
                isValid
                onChange={onSelect}
              />
            </div>
          )}
        </div>
        {/* 직접입력에서 목록에 있는 도메인으로 되돌아갈 길 — 선택칸이 사라져 있으므로 필요하다. */}
        {custom && !isDisabled && (
          <button
            type="button"
            style={{ background: "none", border: 0, padding: "0.2rem 0", fontSize: "1.3rem", color: "#8C8C8C", textDecoration: "underline" }}
            onClick={() => { setCustom(false); emit(local, ""); }}
          >
            목록에서 선택
          </button>
        )}
        {!isValid && message && (
          <div className="message-box">
            <span className="message">{message}</span>
          </div>
        )}
      </div>
    </div>
  );
}
